import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Shield, Mail, Calendar } from "lucide-react";

const statusColors = {
  ACTIVE: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  GRACE: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  SUSPENDED: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function SettingsPage() {
  const { user } = useAuth();

  return (
    <div className="animate-fade-in space-y-8 max-w-2xl">
      <div>
        <h1 className="font-heading text-2xl font-bold mb-1">Settings</h1>
        <p className="text-muted-foreground text-sm">Manage your account and subscription</p>
      </div>

      <Card className="card-hover">
        <CardHeader>
          <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
            <Mail size={16} className="text-primary" /> Account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Email</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium flex items-center gap-2">
                <Calendar size={14} /> Member since
              </p>
              <p className="text-sm text-muted-foreground">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "N/A"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-hover">
        <CardHeader>
          <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
            <Shield size={16} className="text-primary" /> Subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium mb-1">Current Status</p>
              <Badge className={`${statusColors[user?.subscription_status] || statusColors.ACTIVE}`} data-testid="subscription-status-badge">
                {user?.subscription_status || "ACTIVE"}
              </Badge>
              {user?.subscription_status === 'GRACE' && user?.grace_until && (
                <p className="text-xs text-amber-400 mt-2 font-medium">
                  Active until {new Date(user.grace_until).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              )}
              {user?.subscription_status === 'SUSPENDED' && (
                <p className="text-xs text-red-400 mt-2 font-medium">
                  Subscription ended
                </p>
              )}
            </div>
          </div>
          <div className="rounded-lg bg-secondary/50 border border-border p-4 text-sm">
            <p className="font-medium mb-1">Billing-managed access</p>
            <p className="text-xs text-muted-foreground mb-3">Your status is updated automatically after a verified Stripe payment or subscription event.</p>
            <ul className="text-muted-foreground space-y-1 text-xs">
              <li><span className="text-emerald-400 font-medium">ACTIVE</span> - Profile is visible to everyone.</li>
              <li><span className="text-amber-400 font-medium">GRACE</span> - Profile is visible. Payment is past due.</li>
              <li><span className="text-red-400 font-medium">SUSPENDED</span> - Profile shows a branded suspension page.</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
